import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { useDailyVitals } from "@/hooks/use-daily-vitals";
import { SkeletonLoader, EmptyState } from "@/components/system";

/**
 * 14-day sleep duration bars with 8h reference line.
 */

export function SleepChart() {
  const q = useDailyVitals(14);
  if (q.isLoading) return <SkeletonLoader variant="card" height={220} />;
  const rows = (q.data ?? []).slice().reverse();
  const sleepRows = rows.filter((r) => r.sleep_min != null);
  if (sleepRows.length === 0) {
    return (
      <article style={cardStyle}>
        <EmptyState headline="No sleep data yet." sub="Sleep records arrive nightly via HAE / Garmin." />
      </article>
    );
  }
  const data = rows.map((r) => ({
    date: r.date,
    hours: r.sleep_min != null ? Number(r.sleep_min) / 60 : null,
  }));
  return (
    <article style={cardStyle}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: "var(--type-body)", fontWeight: 500 }}>Sleep · 14 days</h3>
        <span className="mono" style={{ fontSize: 12, color: "var(--fg-3)" }}>target 8h</span>
      </header>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--hair)" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={10}
              tickFormatter={(d) => new Date(d as string).toLocaleDateString(undefined, { weekday: "short" })}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
            />
            <YAxis stroke="var(--fg-4)" fontSize={10} width={28} tickLine={false} axisLine={{ stroke: "var(--hair)" }} />
            <Tooltip contentStyle={{ background: "var(--bg-3)", border: "1px solid var(--hair-2)", borderRadius: "var(--radius-card)", fontSize: 12 }} formatter={(v) => `${Number(v).toFixed(1)} h`} />
            <ReferenceLine y={8} stroke="var(--accent)" strokeDasharray="3 3" />
            <Bar dataKey="hours" fill="var(--accent)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--bg-2)",
  border: "1px solid var(--hair)",
  borderRadius: "var(--radius-card)",
  padding: "var(--space-5)",
};
